/**
 * Pinboard images: resized in the browser, stored by content hash (sha256) in IndexedDB and — while a
 * live session is running — on the MC Planner server so everybody in the room can see them.
 */
import type { CardImage } from "@mcp/plan";
import { idb } from "./idb.ts";
import { serverOrigin, type RoomHeaders } from "./share.ts";

/** where a room's images live; `headers` = the room token of an account world (none for worlds shared before accounts) */
export interface ImageRemote { server: string; room: string; headers?: RoomHeaders }

export const MAX_SIDE = 1600;
export const MAX_BYTES = 5 * 1024 * 1024;
const key = (hash: string) => `img:${hash}`;

async function sha256Hex(data: ArrayBuffer) {
  const d = new Uint8Array(await crypto.subtle.digest("SHA-256", data));
  return Array.from(d, (x) => x.toString(16).padStart(2, "0")).join("");
}

function canvasBlob(c: HTMLCanvasElement, type: string, q: number): Promise<Blob | null> {
  return new Promise((res) => c.toBlob(res, type, q));
}

/** Decode, downscale to ≤ 1600 px, re-encode (WebP, JPEG fallback) and store locally. GIFs are kept as they are. */
export async function prepareImage(file: Blob, name?: string): Promise<CardImage> {
  if (!/^image\//.test(file.type)) throw new Error("That's not an image.");
  let blob: Blob;
  let w: number, h: number;
  const bmp = await createImageBitmap(file).catch(() => { throw new Error("This image format can't be read."); });
  if (file.type === "image/gif" && file.size <= MAX_BYTES) {
    blob = file; w = bmp.width; h = bmp.height; // keep animation
  } else {
    const k = Math.min(1, MAX_SIDE / Math.max(bmp.width, bmp.height));
    w = Math.max(1, Math.round(bmp.width * k)); h = Math.max(1, Math.round(bmp.height * k));
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    c.getContext("2d")!.drawImage(bmp, 0, 0, w, h);
    let out = await canvasBlob(c, "image/webp", 0.85);
    if (!out || out.type !== "image/webp") out = await canvasBlob(c, "image/jpeg", 0.88);
    if (!out) throw new Error("Couldn't convert the image.");
    blob = out;
  }
  bmp.close();
  if (blob.size > MAX_BYTES) throw new Error("The image is still over 5 MB after resizing.");
  const hash = await sha256Hex(await blob.arrayBuffer());
  await idb.set(key(hash), blob);
  urls.set(hash, URL.createObjectURL(blob));
  return { hash, w, h, mime: blob.type, ...(name ? { name: name.slice(0, 200) } : {}) };
}

// ---------- lookup ----------

const urls = new Map<string, string>();
const pending = new Map<string, Promise<string | null>>();

export function serverImageUrl(server: string, room: string, hash: string) {
  return `${serverOrigin(server)}/api/rooms/${encodeURIComponent(room)}/images/${hash}`;
}

/**
 * Object URL for an image: from this browser, else downloaded from the room's server (and kept locally).
 * null = not available (offline and never downloaded).
 */
export function imageUrl(hash: string, remote?: ImageRemote | null): Promise<string | null> {
  const hit = urls.get(hash);
  if (hit) return Promise.resolve(hit);
  const p = pending.get(hash);
  if (p) return p;
  const job = (async () => {
    let blob = await idb.get<Blob>(key(hash));
    if (!blob && remote) {
      try {
        const r = await fetch(serverImageUrl(remote.server, remote.room, hash), { headers: remote.headers ? await remote.headers() : undefined });
        if (r.ok) { blob = await r.blob(); await idb.set(key(hash), blob); }
      } catch { /* offline */ }
    }
    if (!blob) return null;
    const u = URL.createObjectURL(blob);
    urls.set(hash, u);
    return u;
  })().finally(() => pending.delete(hash));
  pending.set(hash, job);
  return job;
}

export async function localImage(hash: string): Promise<Blob | undefined> {
  return idb.get<Blob>(key(hash));
}

// ---------- server sync ----------

/** Upload the images this browser has and the server is missing. Returns how many were uploaded. */
export async function uploadMissing(remote: ImageRemote, hashes: string[]): Promise<number> {
  if (!hashes.length) return 0;
  const base = `${serverOrigin(remote.server)}/api/rooms/${encodeURIComponent(remote.room)}/images`;
  const auth = remote.headers ? await remote.headers() : {};
  const r = await fetch(`${base}/have`, { method: "POST", headers: { ...auth, "Content-Type": "application/json" }, body: JSON.stringify({ hashes }) });
  if (!r.ok) throw new Error(`image sync failed (${r.status})`);
  const { missing } = (await r.json()) as { missing: string[] };
  let n = 0;
  for (const h of missing) {
    const blob = await localImage(h);
    if (!blob) continue; // someone else has it; they'll upload it
    const put = await fetch(`${base}/${h}`, { method: "PUT", headers: { ...auth, "Content-Type": blob.type || "image/webp" }, body: blob });
    if (put.ok) n++;
    else if (put.status === 507 || put.status === 403) throw new Error((await put.json().catch(() => ({ error: "storage full" }))).error);
  }
  return n;
}

// ---------- plan files ----------

function blobToDataUrl(b: Blob): Promise<string> {
  return new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = () => rej(r.error); r.readAsDataURL(b); });
}

/** hash → data: URL for every image this browser has (for "include images" in .mcplan files). */
export async function embedImages(hashes: string[]): Promise<Record<string, string>> {
  const out: Record<string, string> = {};
  for (const h of hashes) {
    const b = await localImage(h);
    if (b) out[h] = await blobToDataUrl(b);
  }
  return out;
}

/** Store images embedded in a plan file. */
export async function importImages(images: Record<string, string> | undefined) {
  if (!images) return 0;
  let n = 0;
  for (const [h, dataUrl] of Object.entries(images)) {
    try {
      const b = await (await fetch(dataUrl)).blob();
      if (b.size <= MAX_BYTES && (await sha256Hex(await b.arrayBuffer())) === h) { await idb.set(key(h), b); n++; }
    } catch { /* skip */ }
  }
  return n;
}

// ---------- link previews ----------

export interface Unfurled { url: string; title: string | null; description: string | null; site: string | null; image: string | null; blocked: boolean }

export async function unfurl(server: string, url: string): Promise<Unfurled> {
  const r = await fetch(`${serverOrigin(server)}/api/unfurl?url=${encodeURIComponent(url)}`);
  const j = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
  if (!r.ok) throw new Error((j as { error?: string }).error ?? `HTTP ${r.status}`);
  return j as Unfurled;
}
